import { useSyncExternalStore } from 'react';

export interface ToastRecord {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'destructive';
  duration?: number;
}

/**
 * Minimal external store (no state library needed for one array). Toasts are
 * pushed from anywhere — a mutation's onError, a copy-to-clipboard handler —
 * without needing to be inside a provider's component tree.
 */
let toasts: ToastRecord[] = [];
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return toasts;
}

function dismiss(id: string) {
  toasts = toasts.filter((t) => t.id !== id);
  emit();
}

function push(input: Omit<ToastRecord, 'id'>) {
  const id = crypto.randomUUID();
  const record: ToastRecord = { duration: 4000, variant: 'default', ...input, id };
  toasts = [...toasts, record];
  emit();

  if (record.duration !== 0) {
    setTimeout(() => dismiss(id), record.duration);
  }

  return id;
}

/**
 * Fire-and-forget notifications. Replaces the inline banner divs the vanilla
 * app sprinkled after every save — one queue, one place each variant is
 * styled, and toasts stack instead of a second save clobbering the first
 * message before anyone reads it.
 *
 * Can be called from outside a component too: `toast({ title: '...' })`.
 */
export function toast(input: Omit<ToastRecord, 'id'>) {
  return push(input);
}

export function useToast() {
  const list = useSyncExternalStore(subscribe, getSnapshot);
  return { toasts: list, toast: push, dismiss };
}
